import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { combineLatest, Subscription } from 'rxjs';
import { OpenSquashService } from '../shared/services/open-squash-service';
import { ControlMatch, CourtStatus } from '../tournament-control-component/models/control-state';
import { Tournament } from '../shared/tournament';
import { environment } from '../../../environments/environment';
import {
  computePlayerStats,
  sortByPriority,
  splitByState,
  getDateMillis,
} from '../shared/util/match-priority.util';

@Component({
  selector: 'app-tournament-display-board',
  imports: [CommonModule],
  templateUrl: './tournament-display-board.html',
  styleUrl: './tournament-display-board.scss',
})
export class TournamentDisplayBoardComponent implements OnInit, OnDestroy {
  private service = inject(OpenSquashService);

  tournamentName = '';
  now = new Date();
  lastUpdated: Date = new Date();
  lastUpdatedSecondsAgo = 0;

  matchesEnCancha: ControlMatch[] = [];
  courtSlots: CourtStatus[] = [];
  totalCourts = 4;
  matchesProximos: ControlMatch[] = [];
  matchesFinalizados: ControlMatch[] = [];

  totalPendientes = 0;
  totalFinalizados = 0;

  expandedProximos = false;
  isTvMode = false;
  readonly PROXIMOS_PREVIEW_COUNT = 6;

  // Configurable por environment: oculta la hora programada del partido en
  // la sección de próximos. Default true para mantener el comportamiento.
  readonly showScheduledTime = environment.showMatchScheduledTime !== false;

  enCanchaLayout: 'grid4' | 'vertical' = 'grid4';
  private readonly LAYOUT_STORAGE_KEY = 'display-board-en-cancha-layout';

  private dataSub!: Subscription;
  private tournamentSub!: Subscription;
  private clockTimer: any;
  private reprocessTimer: any;
  private lastData: { matches: any[]; order: Record<string, number> } = { matches: [], order: {} };

  ngOnInit(): void {
    this.detectTvMode();
    this.loadLayoutPreference();
    this.totalCourts = this.service.getTotalCourts();
    window.addEventListener('resize', this.handleResize);

    this.tournamentSub = this.service.tournament$.subscribe((t: Tournament) => {
      this.tournamentName = t?.name || '';
      // El número de canchas se persiste por torneo en localStorage —
      // refresca al cambiar de torneo.
      this.totalCourts = this.service.getTotalCourts();
    });

    this.dataSub = combineLatest([
      this.service.getMatchesExtended(),
      this.service.getControlOrder$(),
    ]).subscribe(([matches, order]) => {
      this.lastData = { matches, order: order || {} };
      this.lastUpdated = new Date();
      this.process();
    });

    this.clockTimer = setInterval(() => {
      this.now = new Date();
      this.lastUpdatedSecondsAgo = Math.floor(
        (this.now.getTime() - this.lastUpdated.getTime()) / 1000
      );
    }, 1000);

    this.reprocessTimer = setInterval(() => {
      this.process();
    }, 60000);
  }

  ngOnDestroy(): void {
    this.dataSub?.unsubscribe();
    this.tournamentSub?.unsubscribe();
    if (this.clockTimer) clearInterval(this.clockTimer);
    if (this.reprocessTimer) clearInterval(this.reprocessTimer);
    window.removeEventListener('resize', this.handleResize);
  }

  private handleResize = () => this.detectTvMode();

  private detectTvMode(): void {
    // Modo TV: pantallas grandes en landscape donde no hay interacción.
    // Expandimos todo y dejamos que CSS recorte lo que no quepa.
    this.isTvMode = window.matchMedia(
      '(min-width: 1280px) and (orientation: landscape)'
    ).matches;
  }

  setEnCanchaLayout(layout: 'grid4' | 'vertical'): void {
    this.enCanchaLayout = layout;
    try {
      localStorage.setItem(this.LAYOUT_STORAGE_KEY, layout);
    } catch {}
  }

  private loadLayoutPreference(): void {
    try {
      const saved = localStorage.getItem(this.LAYOUT_STORAGE_KEY);
      if (saved === 'grid4' || saved === 'vertical') {
        this.enCanchaLayout = saved;
      }
    } catch {}
  }

  private process(): void {
    const matches = this.lastData.matches;
    if (!matches) return;

    const playerStats = computePlayerStats(matches);
    const { enCancha, pendientes, finalizados } = splitByState(matches);

    const sorted = sortByPriority(pendientes, this.lastData.order, {
      playerStats,
      lastMatches: matches,
      now: Date.now(),
    });

    // Asignar posición global (1-indexed) — coincide con la del tablero de control
    sorted.forEach((m, i) => (m.globalPosition = i + 1));

    // Partidos en cancha agrupados por cancha — orden ascendente fijo por
    // número de cancha. Mantiene cada partido siempre en la misma posición
    // del tablero (no se mueve cuando otro termina), y permite mostrar
    // explícitamente las canchas libres.
    this.matchesEnCancha = [...enCancha].sort(
      (a, b) => this.courtNum(a.court) - this.courtNum(b.court)
    );
    this.courtSlots = this.buildCourtSlots(enCancha);
    this.matchesProximos = sorted;
    // Solo los 4 partidos más recientes (más recientes primero) para no
    // robar espacio a la sección de próximos.
    this.matchesFinalizados = [...finalizados].sort((a, b) => {
      const aMs = (a.matchEndedAt as any)?.toMillis?.() || 0;
      const bMs = (b.matchEndedAt as any)?.toMillis?.() || 0;
      return bMs - aMs;
    }).slice(0, 4);
    this.totalPendientes = pendientes.length;
    this.totalFinalizados = finalizados.length;
  }

  get visibleProximos(): ControlMatch[] {
    // En modo TV mostramos todos; el CSS recorta lo que no quepa con fade.
    if (this.expandedProximos || this.isTvMode) return this.matchesProximos;
    return this.matchesProximos.slice(0, this.PROXIMOS_PREVIEW_COUNT);
  }

  get hasMoreProximos(): boolean {
    return !this.isTvMode && this.matchesProximos.length > this.PROXIMOS_PREVIEW_COUNT;
  }

  toggleProximos(): void {
    this.expandedProximos = !this.expandedProximos;
  }

  // --- Helpers para template ---

  scheduledTime(match: ControlMatch): string {
    const ms = getDateMillis(match.date);
    if (!ms) return '';
    const d = new Date(ms);
    const hours = d.getHours();
    const minutes = d.getMinutes();
    const ampm = hours >= 12 ? 'p. m.' : 'a. m.';
    const h = hours % 12 || 12;
    return `${h}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  }

  isOverdue(match: ControlMatch): boolean {
    const ms = getDateMillis(match.date);
    if (!ms) return false;
    return Date.now() > ms;
  }

  elapsedSince(match: ControlMatch): string {
    const startMs = (match.matchStartedAt as any)?.toMillis?.()
      || getDateMillis(match.date);
    if (!startMs) return '';
    const diffMin = Math.max(0, Math.floor((Date.now() - startMs) / 60000));
    return `${diffMin} min`;
  }

  gamesScore(match: ControlMatch): { p1: number; p2: number } {
    return {
      p1: match.player1GamesWon || 0,
      p2: match.player2GamesWon || 0,
    };
  }

  currentGamePoints(match: ControlMatch): { p1: number; p2: number } | null {
    if (!match.games || match.games.length === 0) return null;
    const last = match.games[match.games.length - 1];
    return {
      p1: last.player1Points || 0,
      p2: last.player2Points || 0,
    };
  }

  finishedResult(match: ControlMatch): string {
    return `${match.player1GamesWon || 0} - ${match.player2GamesWon || 0}`;
  }

  winnerSide(match: ControlMatch): 'p1' | 'p2' | '' {
    if (!match.playerIdWinner) return '';
    if (match.playerIdWinner === match.player1Id) return 'p1';
    if (match.playerIdWinner === match.player2Id) return 'p2';
    return '';
  }

  playerName(match: ControlMatch, side: 'p1' | 'p2'): string {
    return side === 'p1'
      ? (match.player1?.playerName || 'TBD')
      : (match.player2?.playerName || 'TBD');
  }

  getInitials(name: string | undefined): string {
    if (!name) return '??';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  categoryLabel(cat?: string): string {
    if (!cat) return '';
    return cat.charAt(0).toUpperCase() + cat.slice(1);
  }

  roundLabel(groupName?: string): string {
    if (!groupName) return '';
    const g = groupName.toLowerCase();
    if (g === 'finales') return 'Finales';
    if (g === 'cuartos' || g === 'cuartos de final') return 'Cuartos de final';
    if (g === 'semifinales') return 'Semifinales';
    if (g === 'final') return 'Final';
    return groupName;
  }

  matchStateLabel(match: ControlMatch): string {
    const s = (match.state as any) as string;
    if (s === 'JUGANDO') return 'EN JUEGO';
    if (s === 'EN_CANCHA') return 'CALENTAMIENTO';
    return s;
  }

  lastUpdatedText(): string {
    const s = this.lastUpdatedSecondsAgo;
    if (s < 5) return 'hace un instante';
    if (s < 60) return `hace ${s} s`;
    const min = Math.floor(s / 60);
    if (min < 60) return `hace ${min} min`;
    const h = Math.floor(min / 60);
    return `hace ${h} h`;
  }

  trackById(_: number, m: ControlMatch): string {
    return m.id || `${m.player1Id}-${m.player2Id}`;
  }

  trackBySlot(_: number, slot: CourtStatus): string {
    return slot.name;
  }

  /**
   * Convierte el campo `court` (string en Firestore: "1", "2", ...) a número.
   * Devuelve un valor alto si está vacío o no es numérico para que esos
   * partidos queden al final del orden.
   */
  private courtNum(court: string | undefined): number {
    const n = parseInt(court || '', 10);
    return Number.isFinite(n) && n > 0 ? n : Number.MAX_SAFE_INTEGER;
  }

  /**
   * Construye los slots de canchas en orden ascendente (1..totalCourts).
   * Cada slot lleva el partido asignado a esa cancha o `null` si está libre.
   * Si hay partidos en cancha con número > totalCourts (drift de datos) se
   * agregan al final para no perderlos.
   */
  private buildCourtSlots(enCancha: ControlMatch[]): CourtStatus[] {
    const byCourt = new Map<number, ControlMatch>();
    const overflow: ControlMatch[] = [];
    for (const m of enCancha) {
      const n = this.courtNum(m.court);
      if (n >= 1 && n <= this.totalCourts) {
        byCourt.set(n, m);
      } else {
        overflow.push(m);
      }
    }
    const slots: CourtStatus[] = [];
    for (let i = 1; i <= this.totalCourts; i++) {
      slots.push({ name: String(i), match: byCourt.get(i) || null });
    }
    for (const m of overflow) {
      slots.push({ name: m.court || '?', match: m });
    }
    return slots;
  }
}
